import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Col,
  Card,
  CardBody,
  InputGroup,
  Input,
  NavItem,
  NavLink,
  Nav,
} from "reactstrap";
import classnames from "classnames";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import { getRoleForBrokerTables } from "../../../slices/ga/thunk";
import Loading from "../../../Components/Loading";
import EmployeesTable from "../../../Components/Tables/EmployeesTable/EmployeesTable";
import EditEmployeeModal from "../../../Components/modals/EmployeesModal/EditEmployeeModal";
import { getStatusEmployees } from "../../../utils/utils";
import NavTabEmployee from "../../../Components/Tabs/NavTabEmployee";
import SearchBar from "../../../Components/SearchBar/SearchBar";
import DropdownActionsTable from "../../../Components/Dropdown/DropdownActionsTable";
import { downloadEmployeesReportByUserId } from "../../../slices/general/thunk";
import { useCurrentUser } from "../../../slices/auth/hooks";
import { DASHBOARD_USER_ROLES } from "../../../constants";

const DashboardEmployeesBroker = ({ isLoaded, employerId }) => {
  const inputRef = useRef(null);
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const isNational = currentUser?.role === DASHBOARD_USER_ROLES.NATIONAL;
  const isAdmin = currentUser?.role === DASHBOARD_USER_ROLES.ADMIN;

  const { id } = useParams();

  const [activeTab, setActiveTab] = useState("1");
  const [isTerminated, setIsTerminated] = useState(false);
  const [isPending, setIsPending] = useState(false);

  const [loading, setLoading] = useState(false);

  const [employees, setEmployees] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);

  const [totalEmployees, setTotalEmployees] = useState(0);
  const [pageSize, setPageSize] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  const [searchDone, setSearchDone] = useState(false);
  const [lastSearchTerm, setLastSearchTerm] = useState(searchTerm);

  const [selectedEmployee, setSelectedEmployee] = useState({});
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const [countByStatus, setCountByStatus] = useState({
    active: 0,
    terminated: 0,
    pending: 0,
  });

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const toggleTab = (tab, status) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      setIsTerminated(status === "terminated");
      setIsPending(status === "pending");
      setCurrentPage(0);
    }
  };

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timerId);
  }, [searchTerm]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const statusEmployees = getStatusEmployees(isTerminated, isPending);
      const response = await dispatch(
        getRoleForBrokerTables({
          id: employerId || id,
          role: "employees",
          status: statusEmployees,
          page: currentPage,
          query: searchTerm,
        })
      ).unwrap();
      if (response.countByStatus) {
        setCountByStatus({
          active: response.countByStatus.active,
          terminated: response.countByStatus.terminated,
          pending: response.countByStatus.pending,
        });
      }
      setEmployees(response.data);
      setTotalEmployees(response.total);
      setPageSize(response.pageSize);
      setTotalPages(response.pages);
      setSearchDone(true);
      setLoading(false);
    } catch (error) {
      setLoading(true);
      console.log(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isLoaded) {
      if (debouncedSearchTerm === lastSearchTerm) {
        setSearchDone(true);
      } else {
        setSearchDone(false);
      }
      fetchEmployees();
    }
  }, [
    isTerminated,
    isPending,
    debouncedSearchTerm,
    employerId,
    id,
    dispatch,
    currentPage,
  ]);

  const handleEditClick = (employee) => {
    setSelectedEmployee(employee);
    setIsEditModalOpen(true);
  };

  const hanldeRefreshTable = () => {
    fetchEmployees();
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };


  return (
    <React.Fragment>
      <EditEmployeeModal
        open={isEditModalOpen}
        setOpen={setIsEditModalOpen}
        editUser={selectedEmployee}
        setUser={setSelectedEmployee}
        onRefreshTable={hanldeRefreshTable}
      />
      <Container fluid>
        <div
          style={{ paddingTop: "3px" }}
          className="col-12 d-flex justify-content-between mt-4 align-items-center "
        >
          <div className="col-4 d-flex justify-content-end align-items-center">
            <SearchBar
              inputRef={inputRef}
              value={searchTerm}
              onChange={handleSearch}
              placeholder="Filter by name or email"
            />
            {(isAdmin || isNational) && (
              <DropdownActionsTable
                downloadMethod={downloadEmployeesReportByUserId}
                params={{ dashboardUserId: employerId || id }}
              />
            )}
          </div>
        </div>
        <Col xl={12}>
          <NavTabEmployee
            loading={loading}
            toggleTab={toggleTab}
            activeTab={activeTab}
            countByStatus={countByStatus}
          />

          <div className="table-container  position-relative">
            {loading && <Loading />}
            <div className="live-preview">
              <div className="table-responsive ">
                <EmployeesTable
                  activeTab={activeTab}
                  data={employees}
                  searchDone={searchDone}
                  searchParam={debouncedSearchTerm}
                  onRefreshTable={hanldeRefreshTable}
                  onEditClick={handleEditClick}
                  shouldShowImportMessage={false}
                  paginationProps={{
                    itemsCount: employees.length,
                    totalPages: totalPages,
                    totalItems: totalEmployees,
                    currentPage: currentPage,
                    onChangePage: handleChangePage,
                  }}
                  isTerminated={isTerminated}
                />
              </div>
            </div>
          </div>
        </Col>
      </Container>
      <div id="portal-root"></div>
    </React.Fragment>
  );
};

export default DashboardEmployeesBroker;
